import type RootStore from "stores/RootStore";
import { RouteType } from "types/RouteType";
import { throwIfFailed } from "shared/dialogSubmit";
import WhiteboardStore from "./WhiteboardStore";

const whiteboardRoute: RouteType[] = [
  {
    name: "Whiteboard",
    path: "/whiteboard",
    layout: "/app",
    viewportCard: {
      header: {
        enableBackButton: true,
        title: "Whiteboard",
        enableRefreshButton: true,
        component: () => import("./Whiteboard.Header"),
      },
      body: {
        component: () => import("./Whiteboard.Body"),
        grayBackground: true,
      },
    },
    group: "Clients",
    searchAliases: [
      "delivery board",
      "sales queue",
      "weekly board",
      "my week",
      "overdue",
      "slipping",
      "blockers",
      "deal statuses",
    ],
    // #3382: any signed-in staff member opens the board and logs their own
    // week; lanes and week notes stay operator-or-admin, which the API
    // enforces (organization/hooks/rbac requireWriteAccess, whiteboard-items).
    requiresAuth: true,
    requiresPermission: false,
    onEnter: ({ rootStore }) => {
      const store = rootStore.whiteboardStore as WhiteboardStore;
      // Deliberately does NOT clear clientFilter: Client 360's "Open on
      // Whiteboard" sets it via focusClient() right before navigating here.
      // `?view=my-week` / `?view=slipping` open those views (the board otherwise).
      store?.showViewFromLink(rootStore.routerStore?.queryParams?.view);
      store?.load();
      // An action item accepted or completed anywhere re-reads the lanes' work.
      store?.subscribe();
    },
    onExit: ({ rootStore }) => {
      (rootStore.whiteboardStore as WhiteboardStore)?.dispose();
    },
    dialogs: [
      {
        name: "WhiteboardCellDialog",
        title: "Week note and entries",
        submitButtonText: "Save note",
        component: () => import("./dialogs/WhiteboardCellDialog.Body"),
        cancelButton: true,
        height: 560,
        onSubmit: async ({ rootStore }: { rootStore: RootStore }) => {
          // A refused save throws, so the dialog stays open with the reason in
          // its footer (shared/dialogSubmit).
          const store = rootStore.whiteboardStore as WhiteboardStore;
          throwIfFailed(await store.submitCellDialog(), store.actionError, "The note could not be saved.");
        },
        // The note is an operator's or admin's (the API refuses anyone else);
        // everyone else opens the dialog for the week's entries.
        onSubmitValidation: ({ rootStore }: { rootStore: RootStore }) => {
          const store = rootStore.whiteboardStore as WhiteboardStore;
          return !!store.cellDraft && store.canEditLanes();
        },
      },
      {
        name: "WhiteboardRowDialog",
        title: "Whiteboard lane",
        submitButtonText: "Save lane",
        component: () => import("./dialogs/WhiteboardRowDialog.Body"),
        cancelButton: true,
        height: 560,
        // A refused save THROWS, which keeps the dialog open with the reason in
        // its footer (submitRowDialog asks the ServiceStore to reject rather
        // than swallow). Resolving closes it.
        onSubmit: async ({ rootStore }: { rootStore: RootStore }) => {
          const store = rootStore.whiteboardStore as WhiteboardStore;
          throwIfFailed(await store.submitRowDialog(), store.actionError, "The lane could not be saved.");
        },
        onSubmitValidation: ({ rootStore }: { rootStore: RootStore }) =>
          (rootStore.whiteboardStore as WhiteboardStore).rowDraftValid,
      },
      {
        name: "WhiteboardEntryDialog",
        title: "Weekly entry",
        submitButtonText: "Save entry",
        component: () => import("./dialogs/WhiteboardEntryDialog.Body"),
        cancelButton: true,
        height: 560,
        // A refused save throws, so the dialog stays open with the reason in
        // its footer (shared/dialogSubmit).
        onSubmit: async ({ rootStore }: { rootStore: RootStore }) => {
          const store = rootStore.whiteboardStore as WhiteboardStore;
          throwIfFailed(await store.submitItemDialog(), store.actionError, "The entry could not be saved.");
        },
        onSubmitValidation: ({ rootStore }: { rootStore: RootStore }) => {
          const store = rootStore.whiteboardStore as WhiteboardStore;
          return store.itemDraftValid && store.itemDialogEditable();
        },
      },
      {
        name: "WhiteboardStatusesDialog",
        title: "Deal statuses",
        submitButtonText: "Save changes",
        component: () => import("./dialogs/WhiteboardStatusesDialog.Body"),
        cancelButton: true,
        height: 640,
        onSubmit: async ({ rootStore }: { rootStore: RootStore }) => {
          const store = rootStore.whiteboardStore as WhiteboardStore;
          throwIfFailed(await store.saveStatusEdits(), store.statusError, "The deal statuses could not be saved.");
        },
        onSubmitValidation: ({ rootStore }: { rootStore: RootStore }) =>
          (rootStore.whiteboardStore as WhiteboardStore).statusEditsValid,
      },
    ],
  },
];

export default whiteboardRoute;
